import {get} from '@shared/helpers/object'

import {fetchExpectedRewardPoint} from '@web/actions/rewardPoint'
import {FamilyPayFailure, OrderFailureType, ReasonType} from '@web/core/constants'
import {ErrorState} from '@web/slices/error/slice'
import makeSlice from '@web/slices/error/sliceMaker'

import {initOrderSheetByAPI, initResult} from '../../actions/familyPay'

const isOrderAlreadyCreated = (reasonAttributes?: object) => {
    const orderStatusType = get('orderStatusType', reasonAttributes)
    return ['PAYED', 'CANCELED_BY_NOPAYMENT'].includes(orderStatusType)
}

const getValidationFailure = ({
    code,
    message,
    validationFailure,
}: {
    code: string
    message: string
    validationFailure?: {
        message: string
        fallbackMessage: string
        reasonType: string
        reasonAttributes: object
    }
}) => {
    const reasonType = validationFailure?.reasonType ?? ''
    const reasonAttributes = validationFailure?.reasonAttributes ?? {}
    if (reasonType === ReasonType.ORDER_ALREADY_CREATED) {
        return {
            type: OrderFailureType.ORDER_ALREADY_CREATED,
            code: reasonType || code,
            message,
        }
    }
    if (
        ['PAYER_PAY_MEMBER_NO_NOT_MATCHED', 'MEMBER_NO_NOT_MATCHED', 'INVALID_FAMILY_PAY_MEMBER_NO'].includes(
            reasonType,
        )
    ) {
        return {
            type: FamilyPayFailure.PAYER_PAY_MEMBER_NO_NOT_MATCHED,
            code: reasonType || code,
            message,
        }
    }
    if (reasonType === 'INVALID_PRODUCT_FOR_FAMILY_PAY') {
        return {
            type: FamilyPayFailure.FAILED_CHECK_PRODUCT,
            code: reasonType || code,
            message,
        }
    }
    if (isOrderAlreadyCreated(reasonAttributes)) {
        return {
            type: FamilyPayFailure.ORDER_ALREADY_CREATED,
            code: reasonType || code,
            message,
        }
    }
    return {
        type: OrderFailureType.UNKNOWN,
        code: reasonType || code,
        message: message || validationFailure?.fallbackMessage,
    }
}

const slice = makeSlice({
    extraReducers(builder) {
        builder
            .addCase(initOrderSheetByAPI.rejected, (state, {payload}) => {
                state.failureCase = (function (): ErrorState['failureCase'] {
                    const code = payload?.code ?? ''
                    const message = payload?.fallbackMessage ?? ''

                    if (code) {
                        if (code === 'VALIDATION_FAILED') {
                            return getValidationFailure({
                                code,
                                message,
                                validationFailure: payload?.validationFailure,
                            })
                        }
                        if (code === FamilyPayFailure.ORDER_ALREADY_CREATED) {
                            return {
                                type: FamilyPayFailure.ORDER_ALREADY_CREATED,
                                code,
                                message,
                            }
                        }
                        if (code === FamilyPayFailure.FAILED_CHECK_PRODUCT) {
                            return {
                                type: FamilyPayFailure.FAILED_CHECK_PRODUCT,
                                code,
                                message,
                            }
                        }

                        return {
                            type: FamilyPayFailure.UNKNOWN,
                            code,
                            message,
                        }
                    }
                    return {
                        type: FamilyPayFailure.UNKNOWN,
                        code: ReasonType.ORDER_SHEET_NOT_FOUND,
                    }
                })()
            })
            .addCase(fetchExpectedRewardPoint.rejected, (state, {payload}) => {
                if (payload) {
                    if (payload.code === 'VALIDATION_FAILED') {
                        const {type, code, message} = getValidationFailure({
                            code: payload.code,
                            message: payload.fallbackMessage,
                            validationFailure: payload?.validationFailure,
                        })
                        if (type !== FamilyPayFailure.UNKNOWN) {
                            state.failureCase = {
                                type,
                                code,
                                message,
                            }
                        }
                    }
                }
            })
            .addCase(initResult.rejected, (state, {payload}) => {
                if (payload?.reason === 'REDIRECT') {
                    state.failureCase = {
                        type: FamilyPayFailure.REDIRECT,
                        code: ReasonType.ORDER_SHEET_NOT_FOUND,
                    }
                } else {
                    const code = payload?.code ?? ''
                    const message = payload?.fallbackMessage ?? ''
                    state.failureCase = {
                        type: FamilyPayFailure.UNKNOWN,
                        code,
                        message,
                    }
                }
            })
    },
})

export default slice.reducer
